import { getLastestHeats } from "@/pages/meltshop/heats/repositories/heatsRepository";
import { ErrorModal, useShiftNavigator } from "@dexteel/mesf-core";
import { IconButton, InputAdornment, TextField, Box } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { get } from "lodash-es";
import { Heat } from "@/pages/meltshop/models/Heat";

interface SearchComponentProps {
  heatSelected: Heat | null;
  setHeatSelected: (heat: Heat | null) => void;
}

const SearchComponent = ({
  heatSelected,
  setHeatSelected,
}: SearchComponentProps) => {
  const [heats, setHeats] = useState<Heat[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const filteredHeatsRef = useRef<Heat[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const { getShiftDataFromAPI } = useShiftNavigator();
  const HEATS_N = 1000;

  const { handleSubmit, setValue, control } = useForm<Heat>();

  const getHeats = async () => {
    setIsLoading(true);
    try {
      const resp = await getLastestHeats(HEATS_N);
      if (resp.ok) {
        const heatsData = get(resp, "data.tables[0].rows", []);
        // Map API response (Name, Id) to expected Heat interface (HeatName, HeatId)
        const mappedHeats = heatsData.map((heat: any) => ({
          HeatId: heat.Id,
          HeatName: heat.Name,
          ShiftId: heat.ShiftId,
        }));
        setHeats(mappedHeats);
      } else {
        const errorMsg = resp.message || "Failed to load heats";
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = "Error loading heats";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getHeats();
  }, []);

  const onSubmit: SubmitHandler<Heat> = async (data) => {
    if (!data.ShiftId) return;
    const heat = heats.find((h) => h.HeatId === data.HeatId) as Heat | null;
    if (heat) {
      setHeatSelected(heat);
      await getShiftDataFromAPI(null, data.ShiftId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (filteredHeatsRef.current.length === 1) {
        const selectedOption = filteredHeatsRef.current[0];
        setValue("HeatId", selectedOption.HeatId);
        setValue("ShiftId", selectedOption.ShiftId);
        onSubmit(selectedOption);
        inputRef.current?.blur();
      }
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          position: "relative",
          width: "100%",
          "& .MuiInputBase-root": {
            height: "40px",
            borderRadius: "4px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            "&:hover": {
              borderColor: "rgba(0, 0, 0, 0.87)",
            },
            "&:before, &:after": {
              display: "none",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            display: "flex",
            alignContent: "center",
            justifyContent: "flex-start",
          },
          "& .MuiInputBase-input": {
            padding: "0 14px",
            display: "flex",
            alignItems: "center",
            height: "100%",
          },
          "& .MuiInputLabel-root": {
            transform: "translate(14px, 12px) scale(1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(16px, -6px) scale(0.75)",
              backgroundColor: "white",
              padding: "0 4px",
            },
          },
          "& .MuiSelect-select": {
            padding: "8px 14px",
          },
          "& .MuiInputAdornment-root": {
            margin: 0,
            height: "100%",
            display: "flex",
            alignItems: "center",
            "& .MuiIconButton-root": {
              padding: "0 12px",
              height: "100%",
              display: "flex",
              alignItems: "center",
            },
          },
          "& .MuiAutocomplete-input": {
            display: "flex !important",
            alignItems: "center !important",
            height: "100% !important",
            padding: "0 14px !important",
          },
        }}
      >
        <Controller
          name="HeatId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              loading={isLoading}
              options={heats}
              filterOptions={(options, state) => {
                const filtered = options.filter((option) =>
                  option.HeatName?.toLowerCase().includes(
                    state.inputValue.toLowerCase(),
                  ),
                );
                filteredHeatsRef.current = filtered;
                return filtered;
              }}
              getOptionLabel={(option) => option.HeatName ?? "Heat"}
              noOptionsText={isLoading ? "Loading heats..." : "No heats found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  placeholder="Search"
                  variant="outlined"
                  fullWidth
                  onKeyDown={handleKeyDown}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton edge="start" type="submit">
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: params.InputProps.endAdornment,
                    },
                  }}
                />
              )}
              value={
                heats.find(
                  (heat) => heat.HeatName === heatSelected?.HeatName,
                ) || null
              }
              onChange={(_, newValue) => {
                if (newValue) {
                  setValue("HeatId", newValue.HeatId);
                  setValue("ShiftId", newValue.ShiftId);
                  onSubmit(newValue);
                } else {
                  setHeatSelected(null);
                }
              }}
              clearOnEscape
              blurOnSelect
            />
          )}
        />
      </Box>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};

export default SearchComponent;
